import {PaginatedResponse, usePrevious} from '@wallaby-ui/core';
import {debounce, isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {connect, ConnectedProps} from 'react-redux';

import AgGridWrapper from './AgGridWrapper';
import {
  getGridStateId,
  getGridConfig,
  getGridPagination,
  getGridSort,
} from '../grid_selectors';
import {gridSlice} from '../grid_slice';
import {loadGridConfig} from '../grid_thunks';
import {AgGridPagination, Pagination, SelectedRow} from '../grid_types';
import {TableConfig} from 'src/js/model/table_config';
import {RootState} from 'src/js/store';

export interface OwnProps {
  configPath?: string;
  domLayout?: string;
  gridId: string;
  gridStateId?: string;
  rowsData: any | undefined;
  paginationData?: Omit<PaginatedResponse, 'content'>;
  disablePagination?: boolean;
  autoPageSize?: boolean;
  propsGridConfigModel?: TableConfig;
  selectedRow?: SelectedRow;
}

const mapState = (state: RootState, props: OwnProps) => ({
  gridStateId: getGridStateId(state, props),
  gridConfig: getGridConfig(state, props),
  gridSort: getGridSort(state, props),
  gridPagination: getGridPagination(state, props),
});

const mapDispatch = {
  changeGridParams: gridSlice.actions.changeGridParams,
  initGridParams: gridSlice.actions.initGridParams,
  loadGridConfigAC: loadGridConfig,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & OwnProps;

const PropsDataGridContainer: React.FC<Props> = (props) => {
  const {
    changeGridParams,
    configPath,
    rowsData,
    disablePagination = false,
    domLayout = 'normal',
    gridConfig,
    gridId,
    gridPagination,
    gridSort,
    gridStateId,
    initGridParams,
    loadGridConfigAC,
    paginationData,
    propsGridConfigModel,
    autoPageSize,
    selectedRow,
  } = props;

  const [initialized, setInitialized] = useState<boolean>(false);

  const gridConfigModel = useMemo<TableConfig>(() => {
    return propsGridConfigModel ?? new TableConfig(gridConfig);
  }, [gridConfig, propsGridConfigModel]);

  useEffect(() => {
    if (!gridConfig && !propsGridConfigModel) {
      loadGridConfigAC(gridStateId, configPath);
    }
  }, [
    configPath,
    loadGridConfigAC,
    gridConfig,
    gridStateId,
    propsGridConfigModel,
  ]);

  useEffect(() => {
    if (!initialized) {
      initGridParams(gridStateId, {
        params: {defaultPagination: {...gridPagination}},
      });
      setInitialized(true);
    }
  }, [initGridParams, initialized, gridId, gridPagination, gridStateId]);

  const debouncedChangeGridParams = useMemo(
    () => debounce(changeGridParams, 500),
    [changeGridParams]
  );

  const previousRowsData = usePrevious(rowsData);
  const previousGridConfigModel = usePrevious(gridConfigModel);

  const handleSortChange = (sortModel) => {
    if (sortModel) {
      if (!isEqual(sortModel, gridSort)) {
        if (gridId) {
          // change result sorting
          const params = {
            ...(!disablePagination &&
            (!gridPagination || gridPagination.page !== 1)
              ? {page: 1}
              : {}),
            sort: sortModel,
          };

          debouncedChangeGridParams(gridStateId, {params});
        }
      }
    }
  };

  const handlePageSizeChange = (size: number) => {
    if (gridPagination) {
      if (!isEqual(size, gridPagination.pageSize)) {
        if (gridId) {
          // change page size and reset page
          const pagination = {
            ...gridPagination,
            ...(!gridPagination || gridPagination.page !== 1 ? {page: 1} : {}),
            pageSize: size,
          };

          // change component state
          debouncedChangeGridParams(gridStateId, {
            params: {pagination},
          });
        }
      }
    }
  };

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      // TODO: this doesnt work as expected,
      // if (!isEqual(pagination.pageSize, gridPagination?.pageSize)) {
      if (gridId) {
        debouncedChangeGridParams(gridStateId, {params: {pagination}});
      }
      // }
    },
    [debouncedChangeGridParams, gridId, gridStateId]
  );

  const handlePageChange = (page: number) => {
    if (!gridPagination) {
      return;
    }
    if (!isEqual(page, gridPagination.page)) {
      if (gridId) {
        // change page
        const params = {page};

        // change component state
        debouncedChangeGridParams(gridStateId, {params});
      }
    }
  };

  useEffect(() => {
    if (
      !disablePagination &&
      gridPagination &&
      rowsData &&
      previousRowsData !== rowsData
    ) {
      // update pagination according to data
      const reportDataLength = rowsData?.data
        ? rowsData.data.length
        : rowsData?.length;
      const prevReportDataLength = previousRowsData?.data
        ? previousRowsData.data.length
        : previousRowsData?.length;
      if (
        reportDataLength !== prevReportDataLength ||
        gridConfigModel.getId() !== previousGridConfigModel?.getId()
      ) {
        // reset pagination only if reportConfig or length of report data changed
        const pagination = {
          pages: Math.ceil(reportDataLength / gridPagination.pageSize),
          page: 1,
        };
        handlePaginationChange({...gridPagination, ...pagination});
      }
    }
  }, [
    disablePagination,
    gridConfigModel,
    gridPagination,
    handlePaginationChange,
    previousGridConfigModel,
    previousRowsData,
    rowsData,
  ]);

  const agGridPagination: AgGridPagination | undefined = useMemo(() => {
    return (
      gridPagination && {
        activePage: gridPagination.page,
        activeSize: gridPagination.pageSize,
        countInPage: gridPagination.pageSize,
        page: gridPagination.page,
        pageSize: gridPagination.pageSize,
        pages: gridPagination.pages,
        resultCount: rowsData?.length ?? 0,
      }
    );
  }, [gridPagination, rowsData]);

  const paginationProps = disablePagination
    ? {}
    : {
        currentPagination: gridPagination,
        gridPagination,
        onPaginationChange: handlePaginationChange,
        handlePageChange,
        handlePageSizeChange,
        paginationData,
        agGridPagination,
      };

  return (
    <AgGridWrapper
      rowsData={rowsData}
      domLayout={domLayout}
      gridConfig={gridConfigModel}
      gridStateId={gridStateId}
      currentSort={gridSort}
      gridId={gridId}
      onSortChange={handleSortChange}
      disablePagination={disablePagination}
      autoPageSize={autoPageSize}
      selectedRow={selectedRow}
      {...paginationProps}
    />
  );
};

export default connector(PropsDataGridContainer);
